/**
 * Who may see what, stated once — the frontend mirror of `backend/app/core/roles.py`.
 *
 * Role checks used to be inline string comparisons (`role === "coaching_admin"`),
 * which is how a new role goes wrong silently: a screen that asks "is this the
 * coaching admin?" answers NO for a school admin and quietly hides their own
 * controls, and the sidebar fell back to the STUDENT menu for any role it did
 * not know. Ask these helpers instead, so adding a role is one edit here.
 *
 * The backend is the enforcement; this only decides what to show.
 */
import type { UserRole } from "../../services/api";

type MaybeRole = string | null | undefined;

/** Runs one organisation: a coaching or a school. */
export const ORG_ADMIN_ROLES = ["coaching_admin", "school_admin"] as const satisfies readonly UserRole[];

/** Platform or organisation admin. */
export const ADMIN_ROLES = ["super_admin", ...ORG_ADMIN_ROLES] as const satisfies readonly UserRole[];

/**
 * Admins plus the read-only power user: the people who may see other people's
 * records, spend, or broadcast to the whole organisation. A teacher is NOT here
 * until class sections scope what they can see.
 */
export const ELEVATED_ROLES = [...ADMIN_ROLES, "user"] as const satisfies readonly UserRole[];

/** Everyone who is not a learner. */
export const STAFF_ROLES = [...ELEVATED_ROLES, "teacher"] as const satisfies readonly UserRole[];

const within = (list: readonly string[], role: MaybeRole) => role != null && list.includes(role);

export const isPlatformAdmin = (role: MaybeRole) => role === "super_admin";
export const isOrgAdmin = (role: MaybeRole) => within(ORG_ADMIN_ROLES, role);
export const isAdmin = (role: MaybeRole) => within(ADMIN_ROLES, role);
export const isElevated = (role: MaybeRole) => within(ELEVATED_ROLES, role);
export const isStaff = (role: MaybeRole) => within(STAFF_ROLES, role);
export const isTeacher = (role: MaybeRole) => role === "teacher";

/** Runs a school, the one org admin that has classes and sections. */
export const isSchoolAdmin = (role: MaybeRole) => role === "school_admin";
/** A school's staff: who shares Library material with classes and works on its courses. */
export const isSchoolStaff = (role: MaybeRole) => isSchoolAdmin(role) || isTeacher(role);

/** A learner enrolled in a school or coaching: the one whose parental consent the organisation records. */
export const isStudent = (role: MaybeRole) => role === "student";
